// TeamSection — perfiles de los fundadores de RE₿EL (home).
// Aditivo: no toca ninguna sección existente. Animación fadeIn + slideUp
// al entrar en viewport (IntersectionObserver) con stagger de 0.15s.
import React, { useEffect, useRef, useState } from "react";
import { Twitter } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { useTeamI18n, TEAM_MEMBERS } from "../../i18n/team";

const TeamCard = ({ member, t, visible, index }) => (
  <div
    data-testid={`team-card-${member.id}`}
    className="group flex flex-col items-center rounded-2xl border p-8 text-center transition-all duration-700"
    style={{
      background: "#0a0a0f",
      borderColor: "rgba(109, 40, 217, 0.3)",
      borderWidth: "0.5px",
      borderRadius: 16,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(32px)",
      transitionDelay: `${index * 0.15}s`,
      transitionProperty: "opacity, transform, box-shadow",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 20px rgba(109, 40, 217, 0.2)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
  >
    <img
      src={member.photo}
      alt={member.name}
      width={200}
      height={200}
      loading="lazy"
      className="h-[200px] w-[200px] rounded-2xl border border-white/10 object-cover object-top"
      data-testid={`team-photo-${member.id}`}
    />
    <h3 className="mt-5 text-white" style={{ fontSize: 20, fontWeight: 600 }}>
      {member.name}
    </h3>
    <span
      className="mt-2 inline-block rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em]"
      style={{
        color: "#f97316",
        borderColor: "rgba(249,115,22,0.35)",
        background: "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(109,40,217,0.10))",
      }}
      data-testid={`team-role-${member.id}`}
    >
      {t[member.roleKey]}
    </span>
    <p className="mt-4 max-w-sm leading-relaxed" style={{ fontSize: 13, color: "#9ca3af" }}>
      {t[member.descKey]}
    </p>
    {member.twitter && (
      <a
        href={member.twitter}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[11px] font-bold text-white/70 transition-all hover:-translate-y-0.5 hover:border-[#f97316]/60 hover:text-[#f97316]"
        data-testid={`team-x-${member.id}`}
      >
        <Twitter className="h-3 w-3" /> {t.connectOn} · {member.twitterHandle}
      </a>
    )}
  </div>
);

export const TeamSection = () => {
  const { lang } = useLanguage();
  const t = useTeamI18n(lang);
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative px-6 py-20 md:py-28" data-testid="team-section" style={{ background: "#0a0a0f" }}>
      <div className="mx-auto max-w-5xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: "#a855f7" }}>
          {t.sectionTag}
        </p>
        <h2 className="mt-3 text-3xl font-black tracking-tight text-white md:text-4xl" data-testid="team-title">
          {t.title}
        </h2>
        <p className="mt-2 text-sm text-white/50">{t.subtitle}</p>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {TEAM_MEMBERS.map((m, i) => (
            <TeamCard key={m.id} member={m} t={t} visible={visible} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
